import axios from 'axios';
import catchify from 'catchify';
import set from 'lodash/set';
import get from 'lodash/get';

import { SMART_COLLECTIONS_URL } from '@/constants/api';
import {
  GET_LIST_REQUEST_ERROR_MESSAGE,
  ADD_DEAL_REQUEST_ERROR_MESSAGE,
  ADD_TO_CART_REQUEST_ERROR_MESSAGE,
  REMOVE_DEAL_REQUEST_ERROR_MESSAGE,
  CREATE_LIST_REQUEST_ERROR_MESSAGE,
  DUPLICATE_LIST_REQUEST_ERROR_MESSAGE,
  DELETE_LIST_REQUEST_ERROR_MESSAGE,
  WISHLISTS_HEADER_REQUEST_ERROR_MESSAGE,
  UPDATE_LIST_REQUEST_ERROR_MESSAGE,
} from '@/constants/wishlist.js';
import { getCSRFCookie } from '~/util/global';

const buildApiRequest = (axiosInstance = axios, url, method = 'get', data = {}) => (
  catchify(axiosInstance({
    data,
    url,
    method,
    withCredentials: true,
    headers: {
      'X-CSRFToken': getCSRFCookie(),
    },
  }))
);

const initialState = () => ({
  wishlistExperiment: 'disabled',
  wishlist: null,
  wishlists: [],

  // Number of times a deal was added to a wishlist
  wishlistsCount: 0,
  errorMessage: '',
  isProcessing: false,
  isFetchingWishlists: false,
  nextSlug: '',
});

export const mutations = {
  setWishlistExperiment(state, value) {
    set(state, 'wishlistExperiment', value);
  },

  setWishlist(state, value) {
    set(state, 'wishlist', value);
  },

  setWishlists(state, value) {
    set(state, 'wishlists', value);
  },

  setWishlistCount(state, value) {
    set(state, 'wishlistsCount', value);
  },

  setErrorMessage(state, value) {
    set(state, 'errorMessage', value);
  },

  setIsProcessing(state, value) {
    set(state, 'isProcessing', value);
  },

  setIsFetchingWishlists(state, value) {
    set(state, 'isFetchingWishlists', value);
  },

  setNextSlug(state, value) {
    set(state, 'nextSlug', value);
  },
};

export const getters = {
  wishlistExperiment: (state) => state.wishlistExperiment,
  wishlist: (state) => state.wishlist,
  wishlists: (state) => state.wishlists,
  wishlistsCount: (state) => state.wishlistsCount,
  errorMessage: (state) => state.errorMessage,
  isProcessing: (state) => state.isProcessing,
  isFetchingWishlists: (state) => state.isFetchingWishlists,
  nextSlug: (state) => state.nextSlug,
};

export const actions = {
  async getWishlists({ commit, dispatch, state }) {
    if (state.isFetchingWishlists) {
      return;
    }

    const response = await dispatch('sendWishlistRequest',
      {
        url: '/api/wishlists/',
        errorMessage: GET_LIST_REQUEST_ERROR_MESSAGE,
      });

    commit('setWishlists', get(response, 'data', []));
  },

  async fetchWishlistsStatByDeal({ commit, dispatch }, dealId) {
    const response = await dispatch('sendWishlistRequest',
      {
        url: `/api/v2/deals/${dealId}/stats/`,
      });

    commit('setWishlistCount', response.data?.wishlists_count || 0);
  },

  async getWishlistsHeaderData({ commit, dispatch }, slug) {
    if (!slug) {
      commit('setErrorMessage', 'No wishlist provided');
      return;
    }

    const response = await dispatch('sendWishlistRequest',
      {
        url: `${SMART_COLLECTIONS_URL}/wishlists/header_details/?template_slug=${slug}`,
        errorMessage: WISHLISTS_HEADER_REQUEST_ERROR_MESSAGE,
      });
    commit('setWishlist', get(response, 'data', null));
  },

  async saveToList({ dispatch }, data) {
    const { wishlistDealId, removeFromList } = data;

    // Remove from previous wishlist
    if (removeFromList && wishlistDealId) {
      await dispatch('removeFromList', { wishlistDealId });
    }

    await dispatch('sendWishlistRequest',
      {
        url: '/api/wishlists/deals/manage/',
        method: 'post',
        data,
        errorMessage: ADD_DEAL_REQUEST_ERROR_MESSAGE,
      });
  },

  async removeFromList({ commit, dispatch }, { wishlistDealId }) {
    if (wishlistDealId) {
      await dispatch('sendWishlistRequest',
        {
          url: `/api/wishlists/deals/manage/${wishlistDealId}/`,
          method: 'delete',
          errorMessage: REMOVE_DEAL_REQUEST_ERROR_MESSAGE,
        });
    } else {
      commit('setErrorMessage', REMOVE_DEAL_REQUEST_ERROR_MESSAGE);
    }
  },

  async createWishlist({ dispatch }, data) {
    await dispatch('sendWishlistRequest',
      {
        url: '/api/wishlists/',
        method: 'post',
        data,
        errorMessage: CREATE_LIST_REQUEST_ERROR_MESSAGE,
      });
  },

  async updateWishlist({ commit, dispatch }, data) {
    const { originalWishlistId } = data;
    if (!originalWishlistId) {
      return;
    }

    const response = await dispatch('sendWishlistRequest',
      {
        url: `/api/wishlists/${originalWishlistId}/`,
        method: 'patch',
        data,
        errorMessage: UPDATE_LIST_REQUEST_ERROR_MESSAGE,
      });

    commit('setWishlist', get(response, 'data.wishlist', null));
  },

  async deleteWishlist({ dispatch }, { wishlistId }) {
    if (!wishlistId) {
      return;
    }

    await dispatch('sendWishlistRequest',
      {
        url: `/api/wishlists/${wishlistId}/`,
        method: 'delete',
        errorMessage: DELETE_LIST_REQUEST_ERROR_MESSAGE,
      });
  },

  async duplicateWishlist({ commit, dispatch }, data) {
    const { originalWishlistId } = data;
    if (!originalWishlistId) {
      return;
    }

    const response = await dispatch('sendWishlistRequest',
      {
        url: `/api/wishlists/${originalWishlistId}/duplicate/`,
        method: 'post',
        data,
        errorMessage: DUPLICATE_LIST_REQUEST_ERROR_MESSAGE,
      });

    const newSlug = response?.data?.slug;

    if (!newSlug) {
      commit('setErrorMessage', DUPLICATE_LIST_REQUEST_ERROR_MESSAGE);
    }

    commit('setNextSlug', newSlug);
  },

  // Add all wishlist deals to cart
  async addWishlistToCart({ dispatch }, wishlistId) {
    await dispatch('sendWishlistRequest',
      {
        url: `/api/wishlists/${wishlistId}/add_all_to_cart/`,
        method: 'post',
        errorMessage: ADD_TO_CART_REQUEST_ERROR_MESSAGE,
      });
  },

  setWishlistCount({ commit }, { value }) {
    commit('setWishlistCount', value);
  },

  setWishlist({ commit }, value) {
    commit('setWishlist', value);
  },

  setPreProcessVals({ commit }) {
    commit('setErrorMessage', '');
    commit('setIsProcessing', true);
  },

  async sendWishlistRequest({ commit, dispatch }, requestContext) {
    const {
      url, method = 'get', data = {}, errorMessage,
    } = requestContext;

    let res = {};

    dispatch('setPreProcessVals');

    try {
      const [err, response] = await buildApiRequest(this.$axios, url, method, data);

      if (err) {
        commit('setErrorMessage', errorMessage);
      }

      res = response;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Error sending wishlist request: ', e);
      // ensure generic error is populated
      commit('setErrorMessage', errorMessage);
    } finally {
      commit('setIsProcessing', false);
    }

    return res;
  },
};

export { initialState as state };
